// Where a field's value comes from, and how to settle it when both sources answer.
//
// Any field can be sourced from the submission documents, from the web, or from both.
// These are two independent decisions, which is why this is not one mode enum:
//
//   sources            which sources are allowed at all
//   prefer             when BOTH produced a value, which one is used
//   flag_disagreement  when BOTH produced a value and they differ, raise a flag
//
// When only one source produces a value, that value is used regardless of `prefer` — a
// preference only means something when there is a choice to make. When neither produces
// one, the field is left empty.
//
// Mirrors FieldSourcing in services/ai_agents/submission_triage/definitions.py; the two
// are pinned together by test_field_sourcing_matches_the_frontend.

export const SOURCE_SUBMISSION = "submission";
export const SOURCE_WEB = "web";

export const defaultSourcing = () => ({
  sources: [SOURCE_SUBMISSION],
  prefer: SOURCE_SUBMISSION,
  flag_disagreement: false,
});

/** Tolerates a missing or half-written config from an older saved ruleset. */
export const normaliseSourcing = (sourcing) => {
  const raw = sourcing || {};
  const sources = Array.isArray(raw.sources) ? raw.sources : [];
  return {
    // An empty list means nothing was configured, which is submission-only.
    sources: sources.length ? sources : [SOURCE_SUBMISSION],
    prefer: raw.prefer === SOURCE_WEB ? SOURCE_WEB : SOURCE_SUBMISSION,
    flag_disagreement: Boolean(raw.flag_disagreement),
  };
};

/**
 * Toggle one source on or off, never leaving a rule with none.
 *
 * A rule with no sources would silently blank the field, so turning off the last remaining
 * source keeps it instead of producing an empty list.
 */
export const toggleSourceIn = (sourcing, source) => {
  const s = normaliseSourcing(sourcing);
  const next = s.sources.includes(source)
    ? s.sources.filter((x) => x !== source)
    : [...s.sources, source];
  return { ...s, sources: next.length ? next : [source] };
};

/** prefer and flag_disagreement only mean something with both sources enabled. */
export const hasBothSources = (sourcing) => {
  const s = normaliseSourcing(sourcing);
  return (
    s.sources.includes(SOURCE_SUBMISSION) && s.sources.includes(SOURCE_WEB)
  );
};

/**
 * The search contract, derived rather than configured.
 *
 * "verify" costs an extra comparison and is only worth it when there is an extracted
 * value to compare against AND a disagreement would actually be reported. Otherwise the
 * agent just sources the field — and is deliberately not shown the extracted value, so it
 * cannot anchor on it.
 */
export const searchModeForSourcing = (sourcing) => {
  const s = normaliseSourcing(sourcing);
  if (!s.sources.includes(SOURCE_WEB)) return "off";
  if (s.sources.includes(SOURCE_SUBMISSION) && s.flag_disagreement)
    return "verify";
  return "web_only";
};

const hasValue = (v) =>
  v != null && !(typeof v === "string" && v.trim() === "");

// Verdicts that mean the web actually produced something usable. not_found and uncertain
// did not, so they fall through to the submission value.
const WEB_PRODUCED = new Set(["sourced", "mismatch", "match"]);

/**
 * Settle a field from its available sources.
 *
 * `check` is the agent search result for this field (null when no search ran). Its
 * apply_value is the enum-resolved, storable form; its verdict carries the model's own
 * judgement on agreement, which is a better signal than comparing strings ourselves — the
 * model treats "ACME Corp" and "Acme Corporation" as agreeing, and a reviewer would too.
 *
 * @returns {{value: *, source: string|null, flagged: boolean, fromBoth: boolean}}
 */
export const resolveSourcedValue = ({ submissionValue, check, sourcing }) => {
  const s = normaliseSourcing(sourcing);
  const submissionAllowed = s.sources.includes(SOURCE_SUBMISSION);
  const webAllowed = s.sources.includes(SOURCE_WEB);

  const haveSubmission = submissionAllowed && hasValue(submissionValue);
  const haveWeb =
    webAllowed &&
    Boolean(check) &&
    WEB_PRODUCED.has(check.verdict) &&
    hasValue(check.apply_value);

  // Both versions are carried on the result whichever one wins. The submission has a
  // single slot per field, so applying a value necessarily discards the other — callers
  // need the pair in order to show it and to record what was not taken.
  const both = {
    submissionValue: haveSubmission ? submissionValue : null,
    // What the web actually said, not the option it was resolved to — the record is a
    // statement about the source, so "Rail Transportation" belongs here even when the
    // only storable form of it was "Freight Rail". A "match" reports its value too:
    // confirming the extraction is still a second version worth keeping.
    webValue: webAllowed
      ? check?.web_value ?? (haveWeb ? check.apply_value : null)
      : null,
  };

  if (haveSubmission && haveWeb) {
    const preferWeb = s.prefer === SOURCE_WEB;
    return {
      ...both,
      value: preferWeb ? check.apply_value : submissionValue,
      source: preferWeb ? SOURCE_WEB : SOURCE_SUBMISSION,
      // Only the model can say whether the two disagree; "mismatch" is that verdict.
      flagged: s.flag_disagreement && check.verdict === "mismatch",
      fromBoth: true,
    };
  }
  // Only one source answered — take it, whatever the preference says.
  if (haveWeb) {
    return {
      ...both,
      value: check.apply_value,
      source: SOURCE_WEB,
      flagged: false,
      fromBoth: false,
    };
  }
  if (haveSubmission) {
    return {
      ...both,
      value: submissionValue,
      source: SOURCE_SUBMISSION,
      flagged: false,
      fromBoth: false,
    };
  }
  return {
    ...both,
    value: null,
    source: null,
    flagged: false,
    fromBoth: false,
  };
};

/**
 * The record of both versions, shaped for FieldSourceValues on the backend.
 *
 * Returns null when there is nothing worth recording — a field only one source could ever
 * have answered has no second version to keep.
 */
export const sourceRecordFor = (resolution, check) => {
  if (!resolution || (!resolution.submissionValue && !resolution.webValue)) {
    return null;
  }
  return {
    submission: resolution.submissionValue ?? null,
    web: resolution.webValue ?? null,
    preferred: resolution.source,
    flagged: Boolean(resolution.flagged),
    verdict: check?.verdict ?? null,
    source_urls: check?.source_urls ?? [],
    reasoning: check?.reasoning ?? null,
    confidence: check?.confidence ?? null,
  };
};
